package gr.tech.GRAttestation.infrastructure.api

import gr.tech.GRAttestation.infrastructure.session.AdminUserSession
import gr.tech.GRAttestation.infrastructure.session.StoreUserSession
import gr.tech.GRAttestation.models.api.ApiError
import gr.tech.GRAttestation.models.api.ApiResponse
import gr.tech.GRAttestation.models.user.UserPreview
import gr.tech.GRAttestation.navigation.AdminSitePage
import gr.tech.GRAttestation.navigation.SitePage
import gr.tech.GRAttestation.util.ROUTES
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import org.w3c.fetch.INCLUDE

import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.xhr.FormData

/*
    Authentication + Authorization
*/

val apiJson = Json { coerceInputValues = true }

/*
    Two Wrapper for all API functions that involve the JWT Cookie's being sent back (credentials).
    Call inside other API functions to ensure the cookie is sent, and if the user is not authenticated,
    they will be redirected to the login page.
 */
suspend inline fun <reified T> safeApiCallStringBody(
    url: String,
    method: String = "GET",
    body: String? = null,
    redirectLogin: Boolean = true,
    redirectTo: String = SitePage.Login.route
): ApiResponse<T> {
    return try {
        val init = RequestInit(
            method = method,
            credentials = RequestCredentials.INCLUDE
        ).apply {
            body?.let { this.body = it }
        }

        val response = window.fetch(url, init).await()
        val jsonText = response.text().await()

        // Handle unauthorized globally
        if (response.status == 401.toShort() &&
            !window.location.href.contains(redirectTo) &&
            redirectLogin
        ) {
            if (StoreUserSession.isLoggedIn()) {
                window.fetch(ROUTES.BACKEND.AUTH.STORE_USER_SIGN_OUT, RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
            } else if (AdminUserSession.isLoggedIn()) {
                window.fetch(ROUTES.BACKEND.AUTH.USER_SIGN_OUT, RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
            }
            window.location.href = redirectTo
            return ApiResponse(
                success = false,
                error = ApiError("unauthorized", "User is not authenticated.")
            )
        }

        return apiJson.decodeFromString<ApiResponse<T>>(jsonText)
    } catch (e: Exception) {
        console.error("API call failed: $url", e)
        ApiResponse(
            success = false,
            error = ApiError("network_error", "Failed to connect. Try again later.")
        )
    }
}

suspend inline fun <reified T> safeApiCallFormData(
    url: String,
    method: String = "POST",
    formData: FormData,
    redirectLogin: Boolean = true,
    redirectTo: String = SitePage.Login.route
): ApiResponse<T> {
    return try {
        val init = RequestInit(
            method = method,
            credentials = RequestCredentials.INCLUDE,
            body = formData
        )

        val response = window.fetch(url, init).await()
        val jsonText = response.text().await()

        if (response.status == 401.toShort() &&
            !window.location.href.contains(redirectTo) &&
            redirectLogin
        ) {
            if (StoreUserSession.isLoggedIn()) {
                window.fetch(ROUTES.BACKEND.AUTH.STORE_USER_SIGN_OUT, RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
            } else if (AdminUserSession.isLoggedIn()) {
                window.fetch(ROUTES.BACKEND.AUTH.USER_SIGN_OUT, RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
            }
            window.location.href = redirectTo
            return ApiResponse(
                success = false,
                error = ApiError("unauthorized", "User is not authenticated.")
            )
        }

        console.log("Status: ${response.status}")
        return apiJson.decodeFromString<ApiResponse<T>>(jsonText)
    } catch (e: Exception) {
        console.error("FormData API call failed", e)
        ApiResponse(
            success = false,
            error = ApiError("network_error", "Failed to connect. Try again later.")
        )
    }
}


suspend fun checkUserJWT(redirectLogin: Boolean = true, redirectTo: String = SitePage.Login.route): ApiResponse<UserPreview> {
    return safeApiCallStringBody(
        url = ROUTES.BACKEND.AUTH.ADMIN_CHECK_AUTH,
        redirectLogin = redirectLogin,
        redirectTo = redirectTo
    )
}

suspend fun adminUserLogout() {
    window.fetch(ROUTES.BACKEND.AUTH.USER_SIGN_OUT, RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
    AdminUserSession.setLoggedOut()
    window.location.href = AdminSitePage.Login.route
}